import React, { useEffect, useMemo, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getBodySystemsList } from '../../../api/body-systems';
import { getMemberBiomarkersResults } from '../../../api/biomarkers';
import Loader from '../../BaseComponents/Loader';
import RangeChart from '../TestResults/PublishedTestResultsTable/RangeChart';
import RangeSummary from '../TestResults/PublishedTestResultsTable/RangeSummary';
import AiMarkdownContent from '../MemberInfoCard/AiMarkdownContent';
import {
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO,
  GenderRangeData,
  HealthSummaryResponseElement,
  ListBodySystemResponseElement,
  UserGenderEnum
} from '../../../types/apiGeneratedTypes';
import { RootState } from '../../../store/store';
import {
  FbsReportData,
  getDetailStatusPillClass
} from '../../FullBodyScan/fullBodyScanReportUtils';
import { getStatusDisplayForItem } from '../../FullBodyScan/fbsFindingStatuses';
import {
  flattenFbsFindings,
  getBiomarkersForBodySystem,
  getBodySystemTabStatusLabel,
  getBodySystemsWithContent,
  getMatchedMriConditionsForBodySystem
} from './bodySystemAnalysisUtils';

interface BodySystemAnalysisContentProps {
  memberId: string;
  reportData: FbsReportData;
  usesFbsFindingStatusV2: boolean;
  healthSummaries: HealthSummaryResponseElement[];
}

const BodySystemAnalysisContent: React.FC<BodySystemAnalysisContentProps> = ({
  memberId,
  reportData,
  usesFbsFindingStatusV2,
  healthSummaries
}) => {
  const { memberData } = useSelector((state: RootState) => state.member);
  const [bodySystems, setBodySystems] = useState<ListBodySystemResponseElement[]>([]);
  const [biomarkerResults, setBiomarkerResults] = useState<
    AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeBodySystemId, setActiveBodySystemId] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!memberId) return;

      try {
        setIsLoading(true);
        const [bodySystemsResponse, biomarkerResultsResponse] = await Promise.all([
          getBodySystemsList(),
          getMemberBiomarkersResults(memberId)
        ]);

        const systemsWithContent = getBodySystemsWithContent(
          bodySystemsResponse.body_systems ?? []
        );
        setBodySystems(systemsWithContent);
        setBiomarkerResults(biomarkerResultsResponse.biomarkers ?? []);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load body system analysis data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [memberId]);

  useEffect(() => {
    if (bodySystems.length === 0) {
      setActiveBodySystemId(null);
      return;
    }

    setActiveBodySystemId((prev) => {
      if (prev && bodySystems.some((bodySystem) => bodySystem.id === prev)) {
        return prev;
      }
      return bodySystems[0].id;
    });
  }, [bodySystems]);

  const summariesByBodySystemId = useMemo(() => {
    const map = new Map<number, HealthSummaryResponseElement>();
    healthSummaries.forEach((summary) => {
      const bodySystemId = summary.body_system?.id;
      if (typeof bodySystemId === 'number') {
        map.set(bodySystemId, summary);
      }
    });
    return map;
  }, [healthSummaries]);

  const flatFindings = useMemo(() => flattenFbsFindings(reportData), [reportData]);

  const activeBodySystem = useMemo(
    () => bodySystems.find((bodySystem) => bodySystem.id === activeBodySystemId) ?? null,
    [bodySystems, activeBodySystemId]
  );

  const activeSummary = useMemo(() => {
    if (!activeBodySystem) return null;
    return summariesByBodySystemId.get(activeBodySystem.id) ?? null;
  }, [activeBodySystem, summariesByBodySystemId]);

  const activeBiomarkers = useMemo(() => {
    if (!activeBodySystem) return [];
    return getBiomarkersForBodySystem(biomarkerResults, activeBodySystem.id);
  }, [activeBodySystem, biomarkerResults]);

  const activeMatchedFbsConditions = useMemo(() => {
    if (!activeBodySystem) return [];
    return getMatchedMriConditionsForBodySystem(
      activeBodySystem.mri_conditions ?? [],
      flatFindings
    );
  }, [activeBodySystem, flatFindings]);

  const activeScanStatus = useMemo(() => {
    if (!activeBodySystem) return 'No scan conditions';
    return getBodySystemTabStatusLabel(
      activeBodySystem.mri_conditions ?? [],
      flatFindings,
      usesFbsFindingStatusV2
    );
  }, [activeBodySystem, flatFindings, usesFbsFindingStatusV2]);

  const getRangeData = (
    biomarker: AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO['biomarker']
  ) => {
    const male = biomarker.male_range_data;
    const female = biomarker.female_range_data;

    const isValid = (data: unknown): data is GenderRangeData =>
      !!data && !Array.isArray(data) && Object.keys(data as object).length > 0;

    if (memberData?.gender === UserGenderEnum.Male && isValid(male)) return male;
    if (memberData?.gender === UserGenderEnum.Female && isValid(female)) return female;
    if (isValid(male)) return male;
    if (isValid(female)) return female;
    return null;
  };

  const getBiomarkerDisplayValue = (
    biomarker: AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO['biomarker'],
    result: AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO['result']
  ) => {
    if (biomarker.biomarker_type === 'BINARY') {
      return result.value === 'POSITIVE' ? 'Positive' : 'Negative';
    }

    if (biomarker.name.toLowerCase().includes('anti-nuclear')) {
      return result.value === 'POSITIVE' ? 'Positive' : 'Negative';
    }

    return `${result.value}${biomarker.measurement_unit ? ` ${biomarker.measurement_unit}` : ''}`;
  };

  const formatResultDate = (value?: string | null) => {
    if (!value) return '-';
    try {
      return formatInTimeZone(new Date(value), 'Europe/London', 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  if (isLoading) {
    return (
      <div className="bsa bsa--loading">
        <Loader />
      </div>
    );
  }

  if (bodySystems.length === 0) {
    return (
      <div className="bsa-empty">
        <p>
          No body systems with linked biomarkers or scan conditions found in the medical library.
        </p>
      </div>
    );
  }

  return (
    <div className="bsa">
      <div className="bsa-tabs" role="tablist" aria-label="Medical library body systems">
        {bodySystems.map((bodySystem) => {
          const tabStatus = getBodySystemTabStatusLabel(
            bodySystem.mri_conditions ?? [],
            flatFindings,
            usesFbsFindingStatusV2
          );
          const isActive = activeBodySystemId === bodySystem.id;
          const summary = summariesByBodySystemId.get(bodySystem.id);
          const scoreLabel = summary?.score?.trim() || null;
          const isCancerScreening =
            bodySystem.title.trim().toLowerCase() === 'cancer screening';

          return (
            <button
              key={bodySystem.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`bsa-tab ${isActive ? 'bsa-tab--active' : ''}`}
              onClick={() => setActiveBodySystemId(bodySystem.id)}
            >
              <span className="bsa-tab__name">{bodySystem.title}</span>
              <span className="bsa-tab__status">
                {scoreLabel
                  ? `Score: ${scoreLabel}`
                  : isCancerScreening && tabStatus === 'No abnormality detected'
                    ? 'Score not generated'
                    : tabStatus}
              </span>
            </button>
          );
        })}
      </div>

      {activeBodySystem ? (
        <div className="bsa-content">
          <div className="bsa-header">
            <div className="bsa-header__title-row">
              {activeBodySystem.icon_url ? (
                <img src={activeBodySystem.icon_url} alt="" className="bsa-header__icon" />
              ) : null}
              <div>
                <h3 className="bsa-header__title">{activeBodySystem.title}</h3>
              </div>
            </div>

            <div className="bsa-summary-grid">
              <div className="bsa-summary-card">
                <span className="bsa-summary-card__label">Blood biomarkers</span>
                <span className="bsa-summary-card__value">{activeBiomarkers.length}</span>
              </div>
              <div className="bsa-summary-card">
                <span className="bsa-summary-card__label">FBS conditions</span>
                <span className="bsa-summary-card__value">
                  {activeMatchedFbsConditions.length}
                </span>
              </div>
              <div className="bsa-summary-card">
                <span className="bsa-summary-card__label">Scan status</span>
                <span className="bsa-summary-card__value bsa-summary-card__value--text">
                  {activeScanStatus}
                </span>
              </div>
              <div className="bsa-summary-card bsa-summary-card--score">
                <span className="bsa-summary-card__label">AI score</span>
                <span className="bsa-summary-card__value bsa-summary-card__value--text">
                  {activeSummary?.score?.trim() || 'Not generated'}
                </span>
              </div>
            </div>
          </div>

          <section className="bsa-section">
            <div className="bsa-section__header">
              <h4>AI summary</h4>
            </div>
            {activeSummary?.details?.trim() ? (
              <div className="bsa-ai-summary">
                <AiMarkdownContent content={activeSummary.details} />
              </div>
            ) : (
              <p className="bsa-section__empty">
                No AI summary yet for this body system. Use Generate to create score and summary
                for all body systems.
              </p>
            )}
          </section>

          <section className="bsa-section">
            <div className="bsa-section__header">
              <h4>Blood biomarkers</h4>
              <span className="bsa-section__count">
                {activeBiomarkers.length}{' '}
                {activeBiomarkers.length === 1 ? 'result' : 'results'}
              </span>
            </div>

            {activeBiomarkers.length > 0 ? (
              <div className="bsa-table">
                <div className="bsa-table__row bsa-table__row--head bsa-table__row--biomarkers">
                  <div>Biomarker name</div>
                  <div>Appointment date</div>
                  <div>Value</div>
                  <div>Chart</div>
                  <div>Summary</div>
                </div>
                {activeBiomarkers.map((entry) => {
                  const rangeData = getRangeData(entry.biomarker);

                  return (
                    <div
                      key={entry.biomarker.id}
                      className="bsa-table__row bsa-table__row--biomarkers"
                    >
                      <div className="bsa-table__biomarker-name">
                        <span>{entry.biomarker.name}</span>
                      </div>
                      <div>{formatResultDate(entry.result.appointment_date)}</div>
                      <div className="bsa-table__value">
                        {getBiomarkerDisplayValue(entry.biomarker, entry.result)}
                      </div>
                      <div className="bsa-table__chart">
                        {rangeData ? (
                          <RangeChart
                            biomarker={entry.biomarker}
                            value={entry.result.value}
                            scale={rangeData}
                          />
                        ) : (
                          <span className="bsa-table__muted">-</span>
                        )}
                      </div>
                      <div className="bsa-table__summary">
                        {rangeData ? (
                          <RangeSummary
                            biomarker={entry.biomarker}
                            value={entry.result.value}
                            scale={rangeData}
                          />
                        ) : (
                          <span className="bsa-table__muted">-</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="bsa-section__empty">No biomarker results for this body system.</p>
            )}
          </section>

          <section className="bsa-section">
            <div className="bsa-section__header">
              <h4>FBS conditions</h4>
              <span className="bsa-section__count">
                {activeMatchedFbsConditions.length}{' '}
                {activeMatchedFbsConditions.length === 1 ? 'finding' : 'findings'}
              </span>
            </div>

            {activeMatchedFbsConditions.length > 0 ? (
              <div className="bsa-table">
                <div className="bsa-table__row bsa-table__row--head bsa-table__row--conditions">
                  <div>Condition</div>
                  <div>Notes</div>
                  <div>Status</div>
                  <div>Description</div>
                </div>
                {activeMatchedFbsConditions.map(({ condition, finding }) => {
                  const statusDisplay = getStatusDisplayForItem(
                    finding.item,
                    usesFbsFindingStatusV2
                  );

                  return (
                    <div key={condition.id} className="bsa-table__row bsa-table__row--conditions">
                      <div className="bsa-table__condition-name">{finding.condition}</div>
                      <div className="bsa-table__notes">
                        {finding.detailedNote?.trim() ? finding.detailedNote : '-'}
                      </div>
                      <div>
                        <span
                          className={`bsa-status-pill ${getDetailStatusPillClass(
                            statusDisplay.label,
                            usesFbsFindingStatusV2
                          )}`}
                        >
                          {statusDisplay.label}
                        </span>
                      </div>
                      <div className="bsa-table__description">
                        {finding.description || condition.guidelines || '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="bsa-section__empty">
                No FBS findings in this member&apos;s scan for conditions mapped to this body
                system.
              </p>
            )}
          </section>
        </div>
      ) : (
        <div className="bsa-empty">
          <p>Select a body system to view analysis.</p>
        </div>
      )}
    </div>
  );
};

export default BodySystemAnalysisContent;
